import { useState } from 'react';
import { useGitLabStatus } from '@/hooks/useGitLab';
import { useAIStatus, useProxyStatus, useTestProxy } from '@/hooks/useAI';
import { useRocketChatStatus, useConnectRocketChat } from '@/hooks/useRocketChat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  Settings as SettingsIcon,
  GitBranch,
  Bot,
  MessageSquare,
  CheckCircle,
  XCircle,
  Zap,
  Server,
  Shield,
  RefreshCw,
} from 'lucide-react';

type SettingsTab = 'gitlab' | 'rocketchat' | 'ai' | 'general';

/**
 * Status indicator component
 */
function StatusIndicator({ connected, label }: { connected: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {connected ? (
        <CheckCircle className="h-5 w-5 text-green-500" />
      ) : (
        <XCircle className="h-5 w-5 text-red-500" />
      )}
      <span className={connected ? 'text-green-700' : 'text-red-700'}>
        {label}
      </span>
    </div>
  );
}

/**
 * GitLab Settings Section
 */
function GitLabSettings() {
  const { data: status, isLoading, refetch } = useGitLabStatus();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Connection Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <StatusIndicator
            connected={status?.connected || false}
            label={status?.connected ? 'Connected to GitLab' : 'Not connected'}
          />

          {status?.connected && (
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground">Version</p>
                <p className="font-medium">{status.version || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">User</p>
                <p className="font-medium">@{status.currentUser?.username || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Repositories</p>
                <p className="font-medium">{status.repoCount || 0}</p>
              </div>
            </div>
          )}

          <Button variant="outline" onClick={() => refetch()}>
            Test Connection
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
          <CardDescription>
            GitLab settings are configured via environment variables
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">GITLAB_URL</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">GITLAB_TOKEN</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">GITLAB_WEBHOOK_SECRET</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Rocket.Chat Settings Section
 */
function RocketChatSettings() {
  const { data: status, isLoading, refetch } = useRocketChatStatus();
  const connectMutation = useConnectRocketChat();

  const handleConnect = async () => {
    await connectMutation.mutateAsync();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Connection Status
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <StatusIndicator
                connected={status?.connected || false}
                label={status?.connected ? 'Connected to Rocket.Chat' : 'Not connected'}
              />

              {status?.connected && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Bot Username</p>
                    <p className="font-medium">@{status.botUsername || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bot User ID</p>
                    <p className="font-medium font-mono text-xs">{status.botUserId || 'Unknown'}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => refetch()}>
                  Check Status
                </Button>
                {!status?.connected && (
                  <Button
                    onClick={handleConnect}
                    disabled={connectMutation.isPending}
                  >
                    {connectMutation.isPending ? (
                      <>
                        <LoadingSpinner className="h-4 w-4 mr-2" />
                        Connecting...
                      </>
                    ) : (
                      'Connect'
                    )}
                  </Button>
                )}
              </div>

              {connectMutation.isError && (
                <p className="text-sm text-red-600">
                  Failed to connect: {(connectMutation.error as Error)?.message || 'Unknown error'}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
          <CardDescription>
            Rocket.Chat settings are configured via environment variables
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">ROCKETCHAT_URL</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">ROCKETCHAT_USER</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">ROCKETCHAT_PASSWORD</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Proxy Status Card
 */
function ProxyStatusCard() {
  const { data: proxyStatus, isLoading } = useProxyStatus();
  const testProxy = useTestProxy();

  const handleTest = async () => {
    await testProxy.mutateAsync();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            AI Proxy Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          AI Proxy Status
        </CardTitle>
        <CardDescription>
          Route AI API calls through your proxy server
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Indicator */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {proxyStatus?.enabled ? (
              proxyStatus.healthy ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-green-700 font-medium">Proxy Connected</span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-500" />
                  <span className="text-red-700 font-medium">Proxy Unhealthy</span>
                </>
              )
            ) : (
              <>
                <XCircle className="h-5 w-5 text-gray-400" />
                <span className="text-gray-600 font-medium">Proxy Disabled</span>
              </>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleTest}
            disabled={testProxy.isPending || !proxyStatus?.enabled}
          >
            {testProxy.isPending ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Test Connection
              </>
            )}
          </Button>
        </div>

        {/* Proxy Details */}
        {proxyStatus?.enabled && (
          <div className="grid gap-4 md:grid-cols-4 pt-2">
            <div>
              <p className="text-sm text-muted-foreground">Host</p>
              <p className="font-mono text-sm">{proxyStatus.host || 'Not set'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Port</p>
              <p className="font-mono text-sm">{proxyStatus.port || 'Not set'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Authentication</p>
              <p className="text-sm">{proxyStatus.hasAuth ? 'Enabled' : 'Disabled'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Latency</p>
              <p className="text-sm">
                {proxyStatus.latencyMs ? `${proxyStatus.latencyMs}ms` : 'N/A'}
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {proxyStatus?.error && proxyStatus.enabled && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">
              <strong>Error:</strong> {proxyStatus.error}
            </p>
          </div>
        )}

        {/* Last Tested */}
        {proxyStatus?.testedAt && (
          <p className="text-xs text-muted-foreground">
            Last tested: {new Date(proxyStatus.testedAt).toLocaleString()}
          </p>
        )}

        {/* Configuration Info */}
        {!proxyStatus?.enabled && (
          <div className="bg-muted/50 rounded-md p-3">
            <p className="text-sm text-muted-foreground">
              To enable the proxy, set the following environment variables:
            </p>
            <div className="mt-2 space-y-1 text-xs font-mono">
              <p>AI_PROXY_ENABLED=true</p>
              <p>AI_PROXY_HOST=your-proxy-host</p>
              <p>AI_PROXY_PORT=8888</p>
              <p>AI_PROXY_USERNAME=username</p>
              <p>AI_PROXY_PASSWORD=password</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * AI Settings Section
 */
function AISettings() {
  const { data: status, isLoading: statusLoading } = useAIStatus();

  return (
    <div className="space-y-6">
      {/* Proxy Status Card - New */}
      <ProxyStatusCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            AI Provider Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statusLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="space-y-4">
              <StatusIndicator
                connected={status?.available || false}
                label={status?.available ? 'AI Available' : 'AI Not Available'}
              />

              {status?.preferredProvider && (
                <div>
                  <p className="text-sm text-muted-foreground">Preferred Provider</p>
                  <p className="font-medium capitalize">{status.preferredProvider}</p>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-3">
                {status?.providers.map((provider) => (
                  <Card key={provider.provider} className="bg-muted/50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium capitalize">{provider.provider}</span>
                        {provider.available ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                      {provider.available && provider.latencyMs && (
                        <p className="text-xs text-muted-foreground">
                          Latency: {provider.latencyMs}ms
                        </p>
                      )}
                      {!provider.available && provider.error && (
                        <p className="text-xs text-red-600 truncate">
                          {provider.error}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
          <CardDescription>
            AI settings are configured via environment variables
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">ANTHROPIC_API_KEY</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">OPENAI_API_KEY</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">OLLAMA_BASE_URL</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">AI_MONTHLY_BUDGET_USD</span>
              <code className="bg-muted px-2 py-0.5 rounded">Set in .env</code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * General Settings Section
 */
function GeneralSettings() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            LDAP Configuration
          </CardTitle>
          <CardDescription>
            User sync settings from LDAP
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Sync Group</span>
              <code className="bg-muted px-2 py-0.5 rounded">RoyalEmployees</code>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Sync Trigger</span>
              <span>On server startup</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Note: LDAP sync group will be configurable in a future update.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Scheduler Settings
          </CardTitle>
          <CardDescription>
            Automated job schedules
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Support Validation</span>
              <span>5:00 PM daily</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">GitLab Sync</span>
              <span>Every hour</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Note: Schedule times will be configurable in a future update.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Settings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('gitlab');

  const tabs: { id: SettingsTab; label: string; icon: typeof SettingsIcon }[] = [
    { id: 'gitlab', label: 'GitLab', icon: GitBranch },
    { id: 'rocketchat', label: 'Rocket.Chat', icon: MessageSquare },
    { id: 'ai', label: 'AI', icon: Bot },
    { id: 'general', label: 'General', icon: SettingsIcon },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Configure integrations and system settings
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2"
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'gitlab' && <GitLabSettings />}
      {activeTab === 'rocketchat' && <RocketChatSettings />}
      {activeTab === 'ai' && <AISettings />}
      {activeTab === 'general' && <GeneralSettings />}
    </div>
  );
}
